// Disable TS check because some of the `downshift` types are goofy
// @ts-nocheck
import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useSelect, UseSelectStateChange } from 'downshift';
import { colors } from '@styleseat/brand';

import './Dropdown.module.scss';

// eslint-disable-next-line @typescript-eslint/comma-dangle
type IDropdownProps<T = any, > = {
  Trigger: FunctionComponent;
  children: Array<T>;
  Item?: FunctionComponent;
  selectedItem?: T | null;
  onChange: ({ selectedItem }: { selectedItem: T }) => void;
  testID?: string;
  onFocus?: (focused: boolean) => void;
  onIsOpenChange?: (isOpenNow: boolean) => void;
  itemToString?: (item: T) => string;
  minWidth?: number;
};

/**
 * A Dropdown component using Downshift's `useSelect` hook.
 * The Dropdown component is a basic implementation of the hook and is the
 * basis of the `Select` component, and any number more.
 *
 * @param Trigger - The component which is given props to launch the dropdown
 * @param children - The items in the dropdown (if not strings, need a key)
 * @param Item? - a wrapping component each item is render in
 * @param selectedItem? - useful for any stateful implementations
 * @param handleChange - fires when the selected item changes
 * @param testID? - test ID
 *
 * An example of a stateless actions dropdown
 * ```
 * <Dropdown
 *   Trigger={props => <Button {...props} >Share</Button>}
 * >
 *  <Button key="fb" onClick={handleFBShare}>Share on Facebook</Button>
 *  <Button key="tw" onClick={handleTWShare}>Share on Twitter</Button>
 * </Dropdown>
 * ```
 */
// eslint-disable-next-line @typescript-eslint/comma-dangle
const Dropdown = <T, >({
  Trigger,
  children: items,
  Item = ({ children }) => <React.Fragment>{children}</React.Fragment>,
  onChange,
  selectedItem,
  testID,
  minWidth = 300,
  onFocus,
  onIsOpenChange,
  itemToString = item => (item ? String(item) : ''),
}: IDropdownProps<T>) => {
  const idPrefix = `${testID || 'dropdown-item'}-`;
  const listRef = useRef<HTMLElement>();
  const handleIsOpenChange = useCallback<UseSelectStateChange<T>>(
    (changes: UseSelectStateChange<T>) => {
      if (changes.isOpen) {
        const selectedIndex = items.indexOf(changes.selectedItem);

        if (selectedIndex !== -1 && listRef.current) {
          const itemEl = listRef.current.querySelector(`#${idPrefix}${selectedIndex}`);

          if (itemEl) {
            itemEl.scrollIntoView();
          }
        }
      }

      onIsOpenChange?.(changes.isOpen);
    },
    [
      idPrefix,
      items,
      onIsOpenChange,
    ],
  );
  const data = useSelect({
    items,
    selectedItem,
    onSelectedItemChange: onChange,
    onIsOpenChange: handleIsOpenChange,
    itemToString,
  });
  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = data;

  useEffect(() => {
    if (onFocus) {
      onFocus(isOpen);
    }
  });

  const toggleButtonProps = getToggleButtonProps();
  toggleButtonProps.buttonRef = toggleButtonProps.ref;
  delete toggleButtonProps.ref;
  toggleButtonProps.includeClickEvent = true;

  const menuProps = getMenuProps();

  return (
    <React.Fragment>
      <Trigger selectedItem={selectedItem} {...toggleButtonProps} />
      <div
        // eslint-disable-next-line react/no-unknown-property
        styleName="menu-container"
        data-testid={testID}
        ref={listRef}
      >
        <div
          {...menuProps}
          // eslint-disable-next-line react/no-unknown-property
          styleName={`menu ${items.length && isOpen ? 'open' : ''}`}
          style={isOpen ? { minWidth } : {}}
        >
          {isOpen && (
            items.map((item, index) => {
              const itemString = itemToString(item);
              const itemID = itemString?.replace(/\W/g, '').toLowerCase();
              return (
                <div
                  id={`${idPrefix}${index}`}
                  data-testid={testID && `${testID}-${itemID}`}
                  style={
                    highlightedIndex === index ? { backgroundColor: colors.chill, cursor: 'pointer' } : {}
                  }
                  key={item.key || itemString || item}
                  {...getItemProps({ item, index })}
                >
                  <Item>
                    {itemString}
                  </Item>
                </div>
              );
            })
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default Dropdown;
